// src/pages/CustomerDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

function CustomerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [rides, setRides] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const API_URL = 'https://boda-backend-wuea.onrender.com/api';

  useEffect(() => {
    fetchCustomerDetails();
  }, [id]);

  const fetchCustomerDetails = async () => {
    try {
      setLoading(true);
      
      // Get customer info
      const customerRes = await axios.get(`${API_URL}/admin/all-riders`);
      const customerData = customerRes.data.riders.find(r => r.id === parseInt(id));
      setCustomer(customerData);
      
      // Get ride history
      const ridesRes = await axios.get(`${API_URL}/rider/rides/${id}`);
      setRides(ridesRes.data.rides || []);
      
      // Calculate stats
      const completedRides = ridesRes.data.rides?.filter(r => r.status === 'completed') || [];
      const totalSpent = completedRides.reduce((sum, r) => sum + (r.total_fare || 0), 0);
      
      setStats({
        total_rides: ridesRes.data.rides?.length || 0,
        completed_rides: completedRides.length,
        total_spent: totalSpent,
        avg_rating: completedRides.reduce((sum, r) => sum + (r.rating || 0), 0) / (completedRides.length || 1),
        favorite_vehicle: getMostUsedVehicle(ridesRes.data.rides || []),
      });
      
    } catch (error) {
      console.error('Error fetching customer:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMostUsedVehicle = (rides) => {
    const vehicleCount = {};
    rides.forEach(r => {
      const v = r.vehicle_type || 'boda';
      vehicleCount[v] = (vehicleCount[v] || 0) + 1;
    });
    let max = 0;
    let favorite = 'boda';
    Object.entries(vehicleCount).forEach(([v, count]) => {
      if (count > max) { max = count; favorite = v; }
    });
    return favorite;
  };

  const getStatusBadge = (status) => {
    const colors = {
      completed: { bg: '#dcfce7', color: '#166534', text: 'Completed' },
      in_progress: { bg: '#fef3c7', color: '#92400e', text: 'In Progress' },
      pending: { bg: '#fef3c7', color: '#92400e', text: 'Pending' },
      cancelled: { bg: '#fee2e2', color: '#991b1b', text: 'Cancelled' },
      accepted: { bg: '#dbeafe', color: '#1e40af', text: 'Accepted' },
    };
    const config = colors[status] || { bg: '#f1f5f9', color: '#64748b', text: status };
    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        backgroundColor: config.bg,
        color: config.color,
      }}>
        {config.text}
      </span>
    );
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>Loading customer details...</div>;
  }

  if (!customer) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>Customer not found</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/customers')}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#64748b'
          }}
        >
          ←
        </button>
        <h1 style={{ margin: 0 }}>Customer Profile</h1>
      </div>

      {/* Customer Info Card */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#f97316',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '28px',
                color: 'white',
              }}>
                {customer.full_name?.charAt(0) || 'R'}
              </div>
              <div>
                <h2 style={{ margin: '0 0 4px 0' }}>{customer.full_name || 'Customer'}</h2>
                <p style={{ color: '#64748b', margin: 0 }}>📞 {customer.phone_number}</p>
                <p style={{ color: '#64748b', margin: '4px 0 0 0' }}>✉️ {customer.email || 'No email'}</p>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '14px', color: '#64748b' }}>Joined</div>
            <div style={{ fontWeight: 'bold' }}>{new Date(customer.created_at).toLocaleDateString()}</div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ color: '#64748b', fontSize: '12px' }}>Total Rides</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.total_rides}</div>
        </div>
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ color: '#64748b', fontSize: '12px' }}>Completed</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>{stats.completed_rides}</div>
        </div>
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ color: '#64748b', fontSize: '12px' }}>Total Spent</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f97316' }}>UGX {stats.total_spent.toLocaleString()}</div>
        </div>
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ color: '#64748b', fontSize: '12px' }}>Avg Rating</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>⭐ {stats.avg_rating.toFixed(1)}</div>
        </div>
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ color: '#64748b', fontSize: '12px' }}>Favorite Vehicle</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {stats.favorite_vehicle === 'boda' ? '🛵' : 
             stats.favorite_vehicle === 'boda_electric' ? '⚡' :
             stats.favorite_vehicle === 'car' ? '🚗' : '🚙'}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid #e2e8f0' }}>
        <button
          onClick={() => setActiveTab('overview')}
          style={{
            padding: '12px 20px',
            backgroundColor: activeTab === 'overview' ? '#f97316' : 'transparent',
            color: activeTab === 'overview' ? 'white' : '#64748b',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
          }}
        >
          📋 Overview
        </button>
        <button
          onClick={() => setActiveTab('rides')}
          style={{
            padding: '12px 20px',
            backgroundColor: activeTab === 'rides' ? '#f97316' : 'transparent',
            color: activeTab === 'rides' ? 'white' : '#64748b',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
          }}
        >
          🚗 Ride History ({rides.length})
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          style={{
            padding: '12px 20px',
            backgroundColor: activeTab === 'analytics' ? '#f97316' : 'transparent',
            color: activeTab === 'analytics' ? 'white' : '#64748b',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
          }}
        >
          📊 Analytics
        </button>
      </div>

      {/* Tab Content */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        {activeTab === 'overview' && (
          <div>
            <h3>Recent Activity</h3>
            {rides.length === 0 ? (
              <p style={{ color: '#64748b', textAlign: 'center' }}>No rides yet</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {rides.slice(0, 5).map((ride) => (
                  <div key={ride.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                  }}>
                    <div>
                      <div style={{ fontWeight: 'bold' }}>Ride #{ride.id}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        {ride.pickup_address} → {ride.dropoff_address}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 'bold', color: '#f97316' }}>UGX {ride.total_fare?.toLocaleString()}</div>
                      {getStatusBadge(ride.status)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'rides' && (
          <div>
            <h3>All Rides</h3>
            {rides.length === 0 ? (
              <p style={{ color: '#64748b', textAlign: 'center' }}>No rides yet</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '12px' }}>Ride ID</th>
                    <th style={{ textAlign: 'left', padding: '12px' }}>Pickup → Dropoff</th>
                    <th style={{ textAlign: 'left', padding: '12px' }}>Fare</th>
                    <th style={{ textAlign: 'left', padding: '12px' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '12px' }}>Rating</th>
                    <th style={{ textAlign: 'left', padding: '12px' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {rides.map((ride) => (
                    <tr key={ride.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px' }}>#{ride.id}</td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ fontSize: '12px' }}>
                          {ride.pickup_address?.substring(0, 20)} → {ride.dropoff_address?.substring(0, 20)}
                        </div>
                      </td>
                      <td style={{ padding: '12px', fontWeight: 'bold', color: '#f97316' }}>
                        UGX {ride.total_fare?.toLocaleString()}
                      </td>
                      <td style={{ padding: '12px' }}>{getStatusBadge(ride.status)}</td>
                      <td style={{ padding: '12px' }}>
                        {ride.rating ? `⭐ ${ride.rating}` : '—'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '12px' }}>
                        {new Date(ride.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <div>
            <h3>Ride Analytics</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div>
                <h4>Vehicle Preference</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {['boda', 'boda_electric', 'car', 'car_xl'].map(v => {
                    const count = rides.filter(r => r.vehicle_type === v).length;
                    const total = rides.length || 1;
                    const percentage = (count / total * 100).toFixed(0);
                    return (
                      <div key={v}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                          <span>{v.replace('_', ' ').toUpperCase()}</span>
                          <span>{count} rides ({percentage}%)</span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '6px',
                          backgroundColor: '#e2e8f0',
                          borderRadius: '4px',
                          overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${percentage}%`,
                            height: '100%',
                            backgroundColor: '#f97316',
                            borderRadius: '4px',
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <h4>Ride Status Breakdown</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {['completed', 'in_progress', 'pending', 'cancelled'].map(s => {
                    const count = rides.filter(r => r.status === s).length;
                    const total = rides.length || 1;
                    const percentage = (count / total * 100).toFixed(0);
                    return (
                      <div key={s}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                          <span style={{ textTransform: 'capitalize' }}>{s}</span>
                          <span>{count} ({percentage}%)</span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '6px',
                          backgroundColor: '#e2e8f0',
                          borderRadius: '4px',
                          overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${percentage}%`,
                            height: '100%',
                            backgroundColor: s === 'completed' ? '#10b981' : 
                                           s === 'cancelled' ? '#ef4444' : '#f59e0b',
                            borderRadius: '4px',
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerDetails;